import { useBehavior, useApp, useButton, useTriggerAction } from '@libs/core';
import { AssetsSvgMap, ECalculatorErrorType, TBaseCalculatorData, TCalculatorError, TWidget } from '@libs/types';
import React from 'react';
import { ButtonRowView } from '../../Buttons/ButtonRowView';
import { CoreLayoutView, CoreTextView, ORIENTATION_ENUM, TCoreTextOptions, useThemeHook } from '@core/widgets';
import { VisualView } from '../../Image/VisualView';
import { PopupComponent } from '../../UI/Popup/PopupComponent';
import { TextStyle } from '../../../types/styles';
import { sanitiseServerValue } from '../utils/format/format';
import { ActionNameEnum, InputEnum, THandleButtonClick, THandleUpdateValue } from '../utils/types';

type TCalculatorErrorPalette = {
    calculatorInputError?: TCoreTextOptions;
} & TCoreTextOptions;

type TCalculatorErrorProps = {
    widget: TWidget<TBaseCalculatorData>;
    handleUpdateValue: THandleUpdateValue;
    handleButtonClick?: THandleButtonClick;
    name: InputEnum;
    newInputValue?: string;
    error: TCalculatorError;
    buttonGotItLabel?: string;
    buttonIncreaseLimitLabel?: string;
    buttonChangeAmountLabel?: string;
    buttonChangeDeliveryOptionLabel?: string;
    buttonChangeTransferOptionLabel?: string;
    parentStyleNames?: string[];
};

const CalculatorError = ({
    widget,
    newInputValue,
    name,
    handleUpdateValue,
    error,
    handleButtonClick,
    buttonGotItLabel = '',
    buttonIncreaseLimitLabel = '',
    buttonChangeAmountLabel = '',
    buttonChangeDeliveryOptionLabel = '',
    buttonChangeTransferOptionLabel = '',
    parentStyleNames,
}: TCalculatorErrorProps) => {
    const { setPopup } = useApp();
    const { palette } = useThemeHook<TCalculatorErrorPalette>(widget);
    const errorPalette = palette?.calculatorInputError ?? {};
    const { isDisabled } = useBehavior(widget);
    const { triggerActionsByType, triggerActionsByNameAndType } = useTriggerAction(widget);
    const { handleClickByActionName } = useButton(widget, { isDisabled, triggerActionsByType, triggerActionsByNameAndType });
    const isShowButtonCircle = error?.type !== ECalculatorErrorType.MIN_AMOUNT;
    const onChangeAmountClick = () => {
        if (newInputValue) {
            const isFrom = name === InputEnum.FROM_INPUT;
            handleUpdateValue(sanitiseServerValue(newInputValue), name, isFrom);
        }
        setPopup(null);
    };

    const handleClick = (actionName?: ActionNameEnum) => {
        setPopup(null);
        if (actionName) {
            handleClickByActionName(actionName);
        }
    };

    const getErrorButtons = () => {
        const buttons: Array<React.ReactNode> = [];

        if (error?.increaseLimitOptions) {
            buttons.push(
                <ButtonRowView
                    key={ActionNameEnum.increaseLimit}
                    label={buttonIncreaseLimitLabel}
                    iconName="ic_arrow_up"
                    parentStyleNames={parentStyleNames}
                    onClick={() => handleClick(ActionNameEnum.increaseLimit)}
                />,
            );
        }
        if (error?.changeAmount) {
            buttons.push(
                <ButtonRowView
                    key="changeAmount"
                    label={buttonChangeAmountLabel}
                    iconName="ic_arrow_retry"
                    parentStyleNames={parentStyleNames}
                    onClick={onChangeAmountClick}
                />,
            );
        }
        if (error?.changeDeliveryOptions) {
            buttons.push(
                <ButtonRowView
                    key={ActionNameEnum.onChangeDeliveryOptions}
                    label={buttonChangeDeliveryOptionLabel}
                    iconName="ic_arrow_back"
                    parentStyleNames={parentStyleNames}
                    onClick={() => handleClick(ActionNameEnum.onChangeDeliveryOptions)}
                />,
            );
        }
        if (error?.withdrawButtonVisible) {
            buttons.push(
                <ButtonRowView
                    key="withdrawButtonVisible"
                    label={`${error?.withdrawButtonText}${error?.maxWithdrawValue} ${error?.withdrawCurIso?.toUpperCase() ?? ''}`}
                    iconName="ic_arrow_forward"
                    parentStyleNames={parentStyleNames}
                    onClick={() => {
                        setPopup(null);
                        handleButtonClick?.();
                    }}
                />,
            );
        }
        if (error?.changeTransferOptions) {
            buttons.push(
                <ButtonRowView
                    key={ActionNameEnum.onChangeTransferOptions}
                    label={buttonChangeTransferOptionLabel}
                    iconName="ic_arrow_back"
                    parentStyleNames={parentStyleNames}
                    onClick={() => handleClick(ActionNameEnum.onChangeTransferOptions)}
                />,
            );
        }
        if (error?.gotIt) {
            buttons.push(<ButtonRowView label={buttonGotItLabel} iconName="ic_check" parentStyleNames={parentStyleNames} onClick={() => handleClick()} />);
        }
        return buttons?.map((button, index) => <div key={index}>{button}</div>);
    };

    const handleErrorClick = () => {
        setPopup(
            <PopupComponent
                header={
                    <CoreTextView
                        text={error?.longText ?? ''}
                        styleName="calculatorBsText"
                        parentStyleNames={parentStyleNames}
                        styleProps={{ ...TextStyle.main, main: 'var(--color-black)', textAlign: 'center' }}
                    />
                }
                externalContent={[getErrorButtons()]}
            />,
        );
    };

    return (
        <CoreLayoutView
            styleName="calculatorInputErrorRow"
            parentStyleNames={parentStyleNames}
            styleProps={{
                orientation: ORIENTATION_ENUM.HORIZONTAL,
                lineY: 'center',
                width: 'auto',
                minHeight: 28,
                padding: { top: 8, right: 6, bottom: 8, left: 8 },
                margin: { top: 4 },
            }}
        >
            <CoreTextView
                as="span"
                text={error?.shortText ?? ''}
                styleName="calculatorInputErrorText"
                parentStyleNames={parentStyleNames}
                styleProps={{ ...TextStyle.secondary, main: 'var(--color-pink_100)', ...errorPalette }}
            />
            {isShowButtonCircle && (
                <VisualView
                    as="button"
                    uid={`calculator_input_error_icon_${name}`}
                    iconName={AssetsSvgMap.ic_info_circle_small}
                    defaultIconName={AssetsSvgMap.ic_x}
                    iconWidth={16}
                    iconHeight={16}
                    iconColor="var(--color-pink_100)"
                    parentStyleNames={[...(parentStyleNames ?? []), 'calculatorInputErrorIcon']}
                    handleClick={handleErrorClick}
                />
            )}
        </CoreLayoutView>
    );
};
export default React.memo(CalculatorError);
